import React, { useEffect, useRef } from "react";

const ForestLossRangeComponent = ({
  // We assume that layerConfig is stable (or memoized in the parent)
  layerConfig,
  mapInstance,
  yearRange = { start: 2001, end: 2023 },
  onLoad,
}) => {
  const abortControllersRef = useRef({});
  // This flag helps us call onLoad only once per setup
  const hasSetupRef = useRef(false);

  // Remove a layer and its source (if they exist)
  const cleanupLayer = (id) => {
    if (!mapInstance) return;
    if (mapInstance.getLayer(id)) {
      try {
        mapInstance.removeLayer(id);
      } catch (e) {
        console.warn(`Could not remove layer ${id}:`, e);
      }
    }
    if (mapInstance.getSource(id)) {
      try {
        mapInstance.removeSource(id);
      } catch (e) {
        console.warn(`Could not remove source ${id}:`, e);
      }
    }
  };

  const setupLayer = async (subLayer) => {
    if (!mapInstance || !subLayer?.id || typeof subLayer.getMapId !== "function") {
      console.error("Invalid subLayer config:", subLayer);
      return;
    }

    // Abort any previous request for this subLayer
    if (abortControllersRef.current[subLayer.id]) {
      abortControllersRef.current[subLayer.id].abort();
    }
    const controller = new AbortController();
    abortControllersRef.current[subLayer.id] = controller;

    try {
      // Always remove any existing layer/source first
      cleanupLayer(subLayer.id);

      // Get the tile URL (this function should return a URL string)
      const tileUrl = await subLayer.getMapId(controller.signal, yearRange);
      if (!tileUrl) {
        console.warn(`No tile URL for subLayer ${subLayer.id}`);
        return;
      }

      // Double-check that the source isn’t already added.
      if (!mapInstance.getSource(subLayer.id)) {
        mapInstance.addSource(subLayer.id, {
          type: "raster",
          tiles: [tileUrl],
          tileSize: 256,
        });
      } else {
        console.warn(`Source with id ${subLayer.id} already exists. Skipping addSource.`);
      }

      if (!mapInstance.getLayer(subLayer.id)) {
        mapInstance.addLayer({
          id: subLayer.id,
          type: "raster",
          source: subLayer.id,
          layout: {
            visibility: subLayer.isVisible ? "visible" : "none",
          },
          paint: {
            "raster-opacity": (layerConfig?.opacity ?? 100) / 100,
          },
        });
      } else {
        console.warn(`Layer with id ${subLayer.id} already exists.`);
      }

      console.log(`ForestLossRange: SubLayer ${subLayer.id} added.`);
    } catch (err) {
      if (err.name === "AbortError") {
        console.log(`ForestLossRange: request aborted for ${subLayer.id}`);
      } else {
        console.error(`ForestLossRange: failed to setup subLayer ${subLayer.id}`, err);
      }
    } finally {
      delete abortControllersRef.current[subLayer.id];
    }
  };

  useEffect(() => {
    if (!mapInstance || !layerConfig?.subLayers) return;

    // Remove any existing layer/source for each sub-layer
    layerConfig.subLayers.forEach((sub) => cleanupLayer(sub.id));

    const setupAll = async () => {
      await Promise.all(layerConfig.subLayers.map((s) => setupLayer(s)));
      if (!hasSetupRef.current && onLoad) {
        hasSetupRef.current = true;
        onLoad({ ...layerConfig, yearRange });
      }
    };

    if (mapInstance.isStyleLoaded()) {
      setupAll();
    } else {
      mapInstance.once("load", setupAll);
    }

    return () => {
      layerConfig.subLayers.forEach((sub) => cleanupLayer(sub.id));
      Object.values(abortControllersRef.current).forEach((ctrl) => ctrl.abort());
    };
    // IMPORTANT: We depend only on mapInstance and yearRange here.
  }, [mapInstance, yearRange]);

  return null; // No visible UI
};

export default ForestLossRangeComponent;
